import { useMemo, useState } from "react";
import type { AppLanguage } from "../../constants/dashboard-language";
import LanguageToggleButton from "./LanguageToggleButton";

type DashboardTab = "registry" | "attendance" | "budget" | "payment-package" | "landing-page";

type TabItem = {
  id: DashboardTab;
  label: string;
};

type DashboardTopButtonsProps = {
  tabs: readonly TabItem[];
  activeTab: DashboardTab;
  onTabClick: (tab: DashboardTab) => void;
  onGoHome: () => void;
  onSignOut: () => void;
  isDark: boolean;
  onToggleTheme: () => void;
  language: AppLanguage;
  onChangeLanguage: (next: AppLanguage) => void;
  registryTabLabel: string;
  attendanceTabLabel: string;
  budgetTabLabel: string;
  paymentPackageTabLabel: string;
  landingPageTabLabel: string;
};

export default function DashboardTopButtons({
  tabs,
  activeTab,
  onTabClick,
  onGoHome,
  onSignOut,
  isDark,
  onToggleTheme,
  language,
  onChangeLanguage,
  registryTabLabel,
  attendanceTabLabel,
  budgetTabLabel,
  paymentPackageTabLabel,
  landingPageTabLabel,
}: DashboardTopButtonsProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const activeLabel = useMemo(() => {
    if (activeTab === "attendance") return attendanceTabLabel;
    if (activeTab === "budget") return budgetTabLabel;
    if (activeTab === "payment-package") return paymentPackageTabLabel;
    if (activeTab === "landing-page") return landingPageTabLabel;
    return registryTabLabel;
  }, [
    activeTab,
    attendanceTabLabel,
    budgetTabLabel,
    landingPageTabLabel,
    paymentPackageTabLabel,
    registryTabLabel,
  ]);

  return (
    <div className="rounded-3xl border border-[color:var(--panel-border)] bg-[color:var(--surface)] p-1.5 shadow-[var(--panel-shadow-soft)] sm:rounded-full sm:p-2">
      <div className="flex items-center gap-2 sm:hidden">
        <button
          type="button"
          data-tab-trigger="true"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
          aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          title={isMobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <svg
            viewBox="0 0 24 24"
            className={`h-4 w-4 transition-transform duration-300 ${isMobileMenuOpen ? "rotate-90" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {isMobileMenuOpen ? (
              <>
                <path d="M6 6l12 12" />
                <path d="M18 6 6 18" />
              </>
            ) : (
              <>
                <path d="M4 7h16" />
                <path d="M4 12h16" />
                <path d="M4 17h16" />
              </>
            )}
          </svg>
        </button>
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-[color:var(--foreground)]">
          {activeLabel}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onGoHome}
            data-tab-trigger="true"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            aria-label={language === "kh" ? "ទំព័រដើម" : "Homepage"}
            title={language === "kh" ? "ទំព័រដើម" : "Homepage"}
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M12 3.5a1.75 1.75 0 0 1 1.166.445l7 6.334a1.75 1.75 0 0 1 .584 1.299V19A1.75 1.75 0 0 1 19 20.75h-4.25A1.75 1.75 0 0 1 13 19v-3.25a.75.75 0 0 0-.75-.75h-.5a.75.75 0 0 0-.75.75V19a1.75 1.75 0 0 1-1.75 1.75H5A1.75 1.75 0 0 1 3.25 19v-7.422c0-.492.207-.96.584-1.299l7-6.334A1.75 1.75 0 0 1 12 3.5Z" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onToggleTheme}
            data-tab-trigger="true"
            aria-label="Toggle dark mode"
            aria-pressed={isDark}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            title="Toggle dark mode"
          >
            {isDark ? (
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M12 17.75a.75.75 0 0 1 .75.75V20a.75.75 0 0 1-1.5 0v-1.5a.75.75 0 0 1 .75-.75Zm5.303-2.053 1.061 1.06a.75.75 0 1 1-1.06 1.061l-1.061-1.06a.75.75 0 1 1 1.06-1.061ZM12 5.5A6.5 6.5 0 1 1 5.5 12 6.507 6.507 0 0 1 12 5.5Zm8 5.75a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1 0-1.5Zm-14.5 0a.75.75 0 0 1 0 1.5H4a.75.75 0 0 1 0-1.5Zm11.742-5.614a.75.75 0 0 1 1.06 0 .75.75 0 0 1 0 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM6.758 15.697a.75.75 0 0 1 1.061 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM12 3.25a.75.75 0 0 1 .75.75V5.5a.75.75 0 0 1-1.5 0V4a.75.75 0 0 1 .75-.75Zm-5.303 2.386 1.061 1.06a.75.75 0 0 1-1.06 1.061L5.636 6.697a.75.75 0 1 1 1.06-1.061Z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M20.742 13.045a.75.75 0 0 0-.83-.19 7.39 7.39 0 0 1-2.787.544 7.66 7.66 0 0 1-7.649-7.649c0-.973.187-1.915.544-2.787a.75.75 0 0 0-.95-1 9.1 9.1 0 1 0 11.862 11.861.75.75 0 0 0-.19-.779Z" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onSignOut}
            data-tab-trigger="true"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
            aria-label="Sign out"
            title="Sign out"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
              <path d="M14.78 3.22a.75.75 0 0 1 0 1.06L13.81 5.25h4.44a1.5 1.5 0 0 1 1.5 1.5v10.5a1.5 1.5 0 0 1-1.5 1.5h-4.44l.97.97a.75.75 0 1 1-1.06 1.06l-2.25-2.25a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 1.06l-.97.97h4.44V6.75h-4.44l.97.97a.75.75 0 0 1-1.06 1.06l-2.25-2.25a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0ZM4.5 6a.75.75 0 0 1 .75.75v10.5A.75.75 0 0 1 4.5 18h-1A1.5 1.5 0 0 1 2 16.5v-9A1.5 1.5 0 0 1 3.5 6h1Z" />
            </svg>
          </button>
          <LanguageToggleButton
            language={language}
            onChangeLanguage={onChangeLanguage}
            compact
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
          />
        </div>
      </div>

      <div
        className={`overflow-hidden transition-[max-height,opacity,transform] duration-300 ease-out ${
          isMobileMenuOpen
            ? "mt-2 max-h-[24rem] opacity-100"
            : "max-h-0 opacity-0 sm:mt-0 sm:max-h-none sm:opacity-100"
        } sm:overflow-visible`}
      >
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab;
            return (
              <button
                key={tab.id}
                type="button"
                data-tab-trigger="true"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onTabClick(tab.id);
                }}
                className={`whitespace-nowrap rounded-full px-3 py-2 text-left text-xs font-semibold transition sm:px-5 sm:text-sm ${
                  isActive
                    ? "bg-[color:var(--accent)] text-white shadow-[var(--accent-shadow)]"
                    : "text-[color:var(--foreground)] hover:bg-[color:var(--surface-hover)]"
                }`}
              >
                {tab.id === "attendance"
                  ? attendanceTabLabel
                  : tab.id === "budget"
                    ? budgetTabLabel
                    : tab.id === "landing-page"
                      ? landingPageTabLabel
                    : tab.id === "payment-package"
                      ? paymentPackageTabLabel
                    : registryTabLabel}
              </button>
            );
          })}
          <div className="hidden items-center gap-2 sm:ml-auto sm:flex">
            <button
              type="button"
              onClick={onGoHome}
              data-tab-trigger="true"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              aria-label={language === "kh" ? "ទំព័រដើម" : "Homepage"}
              title={language === "kh" ? "ទំព័រដើម" : "Homepage"}
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M12 3.5a1.75 1.75 0 0 1 1.166.445l7 6.334a1.75 1.75 0 0 1 .584 1.299V19A1.75 1.75 0 0 1 19 20.75h-4.25A1.75 1.75 0 0 1 13 19v-3.25a.75.75 0 0 0-.75-.75h-.5a.75.75 0 0 0-.75.75V19a1.75 1.75 0 0 1-1.75 1.75H5A1.75 1.75 0 0 1 3.25 19v-7.422c0-.492.207-.96.584-1.299l7-6.334A1.75 1.75 0 0 1 12 3.5Z" />
              </svg>
            </button>
            <button
              type="button"
              onClick={onToggleTheme}
              data-tab-trigger="true"
              aria-label="Toggle dark mode"
              aria-pressed={isDark}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              title="Toggle dark mode"
            >
              {isDark ? (
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                  <path d="M12 17.75a.75.75 0 0 1 .75.75V20a.75.75 0 0 1-1.5 0v-1.5a.75.75 0 0 1 .75-.75Zm5.303-2.053 1.061 1.06a.75.75 0 1 1-1.06 1.061l-1.061-1.06a.75.75 0 1 1 1.06-1.061ZM12 5.5A6.5 6.5 0 1 1 5.5 12 6.507 6.507 0 0 1 12 5.5Zm8 5.75a.75.75 0 0 1 0 1.5h-1.5a.75.75 0 0 1 0-1.5Zm-14.5 0a.75.75 0 0 1 0 1.5H4a.75.75 0 0 1 0-1.5Zm11.742-5.614a.75.75 0 0 1 1.06 0 .75.75 0 0 1 0 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM6.758 15.697a.75.75 0 0 1 1.061 1.06l-1.06 1.061a.75.75 0 1 1-1.061-1.06ZM12 3.25a.75.75 0 0 1 .75.75V5.5a.75.75 0 0 1-1.5 0V4a.75.75 0 0 1 .75-.75Zm-5.303 2.386 1.061 1.06a.75.75 0 0 1-1.06 1.061L5.636 6.697a.75.75 0 1 1 1.06-1.061Z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                  <path d="M20.742 13.045a.75.75 0 0 0-.83-.19 7.39 7.39 0 0 1-2.787.544 7.66 7.66 0 0 1-7.649-7.649c0-.973.187-1.915.544-2.787a.75.75 0 0 0-.95-1 9.1 9.1 0 1 0 11.862 11.861.75.75 0 0 0-.19-.779Z" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={onSignOut}
              data-tab-trigger="true"
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--field-border)] bg-[color:var(--card)] text-[color:var(--foreground)] transition hover:bg-[color:var(--surface-hover)]"
              aria-label="Sign out"
              title="Sign out"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true">
                <path d="M14.78 3.22a.75.75 0 0 1 0 1.06L13.81 5.25h4.44a1.5 1.5 0 0 1 1.5 1.5v10.5a1.5 1.5 0 0 1-1.5 1.5h-4.44l.97.97a.75.75 0 1 1-1.06 1.06l-2.25-2.25a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 1.06l-.97.97h4.44V6.75h-4.44l.97.97a.75.75 0 0 1-1.06 1.06l-2.25-2.25a.75.75 0 0 1 0-1.06l2.25-2.25a.75.75 0 0 1 1.06 0ZM4.5 6a.75.75 0 0 1 .75.75v10.5A.75.75 0 0 1 4.5 18h-1A1.5 1.5 0 0 1 2 16.5v-9A1.5 1.5 0 0 1 3.5 6h1Z" />
              </svg>
            </button>
            <div className="hidden sm:block">
              <LanguageToggleButton language={language} onChangeLanguage={onChangeLanguage} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
